import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, ExternalLink, X, ChevronLeft, ChevronRight, CalendarPlus, CalendarDays, MapPin, ArrowDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { upcomingEvents, pastEvents } from "@/data/events";
import { scrollToId } from "@/lib/scroll";

const Lightbox = ({ photos, index, onClose, onPrev, onNext }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Galleria foto"
        data-testid="evento-lightbox"
    >
        <button type="button" onClick={onClose} aria-label="Chiudi la galleria" data-testid="lightbox-close" className="absolute top-5 right-5 p-2.5 rounded-full bg-cream/10 text-cream hover:bg-cream/25 transition-colors duration-300">
            <X size={24} />
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Foto precedente" data-testid="lightbox-prev" className="absolute left-3 sm:left-6 p-2.5 rounded-full bg-cream/10 text-cream hover:bg-cream/25 transition-colors duration-300">
            <ChevronLeft size={26} />
        </button>
        <motion.img
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            src={photos[index].src}
            alt={photos[index].alt}
            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
        />
        <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Foto successiva" data-testid="lightbox-next" className="absolute right-3 sm:right-6 p-2.5 rounded-full bg-cream/10 text-cream hover:bg-cream/25 transition-colors duration-300">
            <ChevronRight size={26} />
        </button>
    </motion.div>
);

const PhotoGrid = ({ photos, onOpen }) => (
    <div className="mt-5 grid grid-cols-2 gap-3">
        {photos.map((p, i) => (
            <button
                key={p.src}
                type="button"
                onClick={() => onOpen(i)}
                aria-label={`Apri la foto: ${p.alt}`}
                data-testid={`evento-photo-${i + 1}`}
                className="group relative overflow-hidden rounded-xl focus:outline-none focus:ring-2 focus:ring-sage"
            >
                <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className={`w-full aspect-[4/3] object-cover ${p.pos || "object-center"} transition-transform duration-500 group-hover:scale-105 cursor-zoom-in`}
                />
            </button>
        ))}
    </div>
);

export const Libro = () => {
    const [lightbox, setLightbox] = useState(null);
    const [lightboxPhotos, setLightboxPhotos] = useState([]);
    const upcoming = upcomingEvents();
    const past = pastEvents();

    const openLightbox = (photos, i) => {
        setLightboxPhotos(photos);
        setLightbox(i);
    };

    return (
        <section id="libro" className="bg-sand/70" data-testid="libro-section">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 md:py-32">
                {upcoming.map((ev) => (
                    <Reveal key={`banner-${ev.id}`} className="mb-12">
                        <button
                            type="button"
                            onClick={() => scrollToId(`#evento-${ev.id}`)}
                            data-testid="evento-banner"
                            className="group w-full flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full bg-petrol text-cream px-6 py-4 text-sm sm:text-base font-semibold shadow-lg shadow-petrol/20 hover:bg-petrol-soft transition-all duration-300"
                        >
                            <CalendarDays size={18} className="shrink-0" />
                            <span>7 dicembre · Firmacopie a Roma, Più libri più liberi</span>
                            <span className="inline-flex items-center gap-1 text-terracotta-light">
                                Scopri <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                            </span>
                        </button>
                    </Reveal>
                ))}

                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <Reveal className="order-2 lg:order-1">
                        <div className="relative mx-auto max-w-md">
                            <div className="overflow-hidden rounded-3xl shadow-2xl shadow-petrol/20 bg-white">
                                <img
                                    src="/images/libro-promo.jpg"
                                    alt="Copertina del libro «Prima di Pensare — Perché facciamo cose che non volevamo fare» di Giovanni De Lorenzo, Armando Editore"
                                    className="w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
                                    loading="lazy"
                                    data-testid="libro-cover"
                                />
                            </div>
                        </div>
                    </Reveal>
                    <div className="order-1 lg:order-2">
                        <Reveal>
                            <Eyebrow>Divulgazione</Eyebrow>
                            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug text-ink" data-testid="libro-title">
                                «Prima di Pensare»
                            </h2>
                            <p className="mt-3 font-serif italic text-xl text-petrol">
                                Perché facciamo cose che non volevamo fare
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-7 text-base sm:text-lg text-[#526063] leading-relaxed">
                                Il Dott. Giovanni De Lorenzo è autore di un libro divulgativo pubblicato da Armando Editore, dedicato a emozioni, relazioni, scelte e benessere: un invito a comprendere ciò che sentiamo per poter scegliere ciò che facciamo.
                            </p>
                            <blockquote className="mt-7 border-l-2 border-terracotta pl-6 font-serif italic text-xl text-petrol leading-relaxed">
                                "Capire ciò che sentiamo ci permette di scegliere ciò che facciamo."
                            </blockquote>
                        </Reveal>

                        {upcoming.map((ev) => (
                            <Reveal key={ev.id} delay={0.15}>
                                <div id={`evento-${ev.id}`} className="mt-9 rounded-2xl bg-white border-2 border-terracotta/40 shadow-md p-6 sm:p-7" data-testid="evento-upcoming-card">
                                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
                                        <CalendarDays size={15} /> Prossimo appuntamento
                                    </p>
                                    <h3 className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-ink">{ev.title}</h3>
                                    <ul className="mt-4 space-y-2.5 text-sm text-ink/80">
                                        <li className="flex items-start gap-3"><CalendarDays size={16} className="text-sage-dark shrink-0 mt-0.5" /> {ev.when}</li>
                                        <li className="flex items-start gap-3"><MapPin size={16} className="text-sage-dark shrink-0 mt-0.5" /> {ev.where}</li>
                                    </ul>
                                    <p className="mt-4 text-sm text-ink/80 leading-relaxed">{ev.text}</p>
                                    {ev.image && (
                                        <button
                                            type="button"
                                            onClick={() => openLightbox([{ src: ev.image, alt: ev.imageAlt }], 0)}
                                            aria-label={`Ingrandisci: ${ev.imageAlt}`}
                                            data-testid="evento-image-btn"
                                            className="mt-5 block w-full overflow-hidden rounded-xl focus:outline-none focus:ring-2 focus:ring-sage"
                                        >
                                            <img src={ev.image} alt={ev.imageAlt} loading="lazy" className="w-full h-auto transition-transform duration-500 hover:scale-[1.02] cursor-zoom-in" />
                                        </button>
                                    )}
                                    {ev.ics && (
                                        <a
                                            href={ev.ics}
                                            download="firmacopie-roma.ics"
                                            data-testid="evento-calendar-btn"
                                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-petrol text-cream px-6 py-3 text-sm font-semibold hover:bg-petrol-soft hover:shadow-lg transition-all duration-300"
                                        >
                                            <CalendarPlus size={16} /> Aggiungi al calendario
                                        </a>
                                    )}
                                </div>
                            </Reveal>
                        ))}

                        {past.length > 0 && (
                            <Reveal delay={0.2}>
                                <div className="mt-9" data-testid="eventi-passati">
                                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sage">
                                        <BookOpen size={15} /> Appuntamenti passati
                                    </p>
                                    <div className="mt-4 space-y-5">
                                        {past.map((ev) => (
                                            <div key={ev.id} className="rounded-2xl bg-white border border-ink/5 shadow-sm p-6 sm:p-7" data-testid={`evento-past-${ev.id}`}>
                                                <h3 className="font-serif text-xl font-semibold text-ink">{ev.title}</h3>
                                                <p className="mt-3 text-sm text-ink/80 leading-relaxed">{ev.text}</p>
                                                <p className="mt-3 text-xs text-[#7A8C8E]">{ev.note || ev.when}</p>
                                                {ev.photos && <PhotoGrid photos={ev.photos} onOpen={(i) => openLightbox(ev.photos, i)} />}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        )}

                        <Reveal delay={0.25}>
                            <a
                                href="https://www.armandoeditore.it/catalogo/prima-di-pensare-perche-facciamo-cose-che-non-volevamo-fare/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="libro-publisher-link"
                                className="mt-7 inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink hover:border-petrol hover:text-petrol hover:bg-petrol-light/40 transition-all duration-300"
                            >
                                Scopri il libro <ExternalLink size={15} />
                            </a>
                        </Reveal>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {lightbox !== null && (
                    <Lightbox
                        photos={lightboxPhotos}
                        index={lightbox}
                        onClose={() => setLightbox(null)}
                        onPrev={() => setLightbox((i) => (i - 1 + lightboxPhotos.length) % lightboxPhotos.length)}
                        onNext={() => setLightbox((i) => (i + 1) % lightboxPhotos.length)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};
